// The client side of /api/organisations (BUILD-BRIEF §4 §6, owned by the organisations agent).
// Every call goes through `api/client.ts` — never a raw fetch in a component.
import { request, qs } from './client.js';
import type {
  ListResponse, Organisation, OrganisationDetail, OrganisationStatus, OrganisationTree,
  OrganisationTreeNode,
} from '../../shared/types.js';

export type { OrganisationTree, OrganisationTreeNode };

/** One row on the Organisations board — the list response's extra figures beyond the row. */
export interface OrganisationListItem extends Organisation {
  people_count: number;
  open_opportunities_count: number;
  open_face_value: string | number;
  /**
   * The round-3b tree rollups (BUILD-BRIEF-R3b §2b). OPTIONAL on purpose: a server that has not
   * grown them sends nothing, and the board then shows the people figure alone rather than a
   * "0 units" that was never counted.
   */
  units_count?: number | null;
  people_in_tree?: number | null;
}

/** The board's rail figures. `monitored` and `signals` are `null`: no producer exists yet
 * (no monitors/signals table in round 1) — never rendered as zero. */
export interface OrganisationsSummary {
  held: number;
  monitored: number | null;
  open_deals: number;
  open_deals_value: string | number;
  signals: number | null;
  clients: number;
}

export interface OrganisationsListResponse extends ListResponse<OrganisationListItem> {
  summary: OrganisationsSummary;
}

/**
 * The last `deep_research` run over this organisation (BUILD-BRIEF-R4 §4). The server ships it
 * BESIDE the organisation row, not on it, because it describes a RUN and not the organisation:
 * its ref, how it ended, and how many proposals it filed.
 */
export interface LastResearch {
  run_id: number;
  run_ref: string;
  status: string;
  /** When the run ended; null while it is still queued or running. The row's own
   *  `last_researched_at` is the stamp the mast prefers. */
  at: string | null;
  proposed: number;
  summary: string | null;
}

/** GET /api/organisations/:id's extra fields beyond the contracted shape. */
export interface OrganisationDetailView extends OrganisationDetail {
  next_move: { headline: string; detail: string } | null;
  last_touch: { ref: string; kind: string; occurred_on: string; title: string } | null;
  days_since_touch: number | null;
  /** The tree rollups the same read counts. Optional: an older server sends nothing. */
  tree?: { units_count: number; people_in_tree: number } | null;
  /** Null when no research run has ever been started on this organisation. */
  last_research?: LastResearch | null;
}

export function listOrganisations(
  params: { q?: string; status?: OrganisationStatus | ''; client?: boolean; tree_of?: number } = {},
): Promise<OrganisationsListResponse> {
  return request(`/organisations${qs({
    q: params.q, status: params.status, client: params.client ? '1' : undefined, tree_of: params.tree_of,
  })}`);
}

// ── the tree (BUILD-BRIEF-R3b §2b) ──────────────────────────────────────────

/**
 * GET /api/organisations/:id/tree — the root, its ancestors (the breadcrumb), and every unit
 * under it to depth 3, each with the figures the SERVER counted for that node alone.
 *
 * The shape is `src/shared/types.ts`'s, read by both sides. A page never derives a tree from a
 * parent id of its own: `parent_id` is one link, and a tree is a recursive query.
 */
export function getOrganisationTree(id: number): Promise<OrganisationTree> {
  return request(`/organisations/${id}/tree`);
}

/**
 * The research stamp for the mast (§4: "last research RUN-0007 · dd.mm.yyyy · 18 proposed").
 *
 * The two halves come from two places, which is what this function exists to hide: the DATE is
 * `organisations.last_researched_at`, a column on the row the harness runner stamps when a run
 * ends done, and the TALLY is `last_research.proposed` on the detail payload BESIDE the row,
 * because it belongs to the run and not to the organisation. So pass the whole detail
 * (`researchStamp(data)`); a bare row still answers with the date.
 *
 * A server that ships neither leaves `proposed` null and the mast prints the date alone — a run
 * nobody counted is not a run that proposed nothing.
 */
export interface ResearchStamp {
  last_researched_at: string | null;
  /** Filed by the last research run. NULL means nobody counted, which is not "nothing was proposed". */
  proposed: number | null;
  /** The run behind the tally, when the payload named one — for the title and a route to it. */
  run: LastResearch | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export function researchStamp(
  source: Organisation | OrganisationDetail | OrganisationDetailView | null | undefined,
): ResearchStamp {
  const payload: Record<string, unknown> = isRecord(source) ? source : {};
  // Either a detail payload (`{ organisation, last_research, … }`) or the bare row.
  const row = isRecord(payload.organisation) ? payload.organisation : payload;
  const run = isRecord(payload.last_research) ? (payload.last_research as unknown as LastResearch) : null;

  const at = typeof row.last_researched_at === 'string'
    ? row.last_researched_at
    : typeof run?.at === 'string' ? run.at : null;

  return {
    last_researched_at: at,
    proposed: run && typeof run.proposed === 'number' && Number.isFinite(run.proposed) ? run.proposed : null,
    run,
  };
}

export function getOrganisation(id: number): Promise<OrganisationDetailView> {
  return request(`/organisations/${id}`);
}

export interface OrganisationInput {
  name: string;
  legal_name?: string | null;
  legal_form?: string | null;
  uid?: string | null;
  domains?: string[];
  aliases?: string[];
  seat?: string | null;
  canton?: string | null;
  sector?: string | null;
  parent_id?: number | null;
  status?: OrganisationStatus;
  is_client?: boolean;
  do_not_contact?: boolean;
  notes?: string | null;
}

export function createOrganisation(input: OrganisationInput): Promise<Organisation> {
  return request('/organisations', { method: 'POST', body: JSON.stringify(input) });
}

export function updateOrganisation(id: number, patch: Partial<OrganisationInput> & { signet_client_id?: number | null }): Promise<Organisation> {
  return request(`/organisations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function archiveOrganisation(id: number): Promise<Organisation> {
  return request(`/organisations/${id}/archive`, { method: 'POST' });
}
